// Reusable Template Form UI
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, Trash2 } from 'lucide-react';

interface IncludedItem {
  icon: React.ElementType;
  label: string;
  included: boolean;
  count?: number;
  color?: string;
}

interface TemplateFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (payload: { name: string; description?: string; isDefault: boolean }) => void;
  onDelete?: () => void;
  isSaving?: boolean;
  includedItems?: IncludedItem[];
  trigger?: React.ReactNode;
  initialData?: { name: string; description?: string; isDefault: boolean };
}

export function TemplateForm({
  open,
  onOpenChange,
  onSave,
  onDelete,
  isSaving = false,
  includedItems = [],
  trigger,
  initialData
}: TemplateFormProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [isDefault, setIsDefault] = useState(initialData?.isDefault || false);

  useEffect(() => {
    if (open && initialData) {
      setName(initialData.name);
      setDescription(initialData.description || '');
      setIsDefault(initialData.isDefault);
    }
  }, [open, initialData]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: description.trim() || undefined, isDefault });
  };

  const activeItems = includedItems.filter((i) => i.included);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger || null}</DialogTrigger>

      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Save as Template</DialogTitle>
          <DialogDescription>
            Save this workflow configuration to quickly start future analyses with the same settings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">Template Name *</Label>
            <Input
              id="template-name"
              placeholder="e.g., Standard Credit Analysis"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-description">Description (Optional)</Label>
            <Textarea
              id="template-description"
              placeholder="Describe when to use this template..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-default"
              checked={isDefault}
              onCheckedChange={(checked) => setIsDefault(checked === true)}
            />
            <Label htmlFor="is-default" className="text-sm font-normal cursor-pointer">
              Set as default template for new analyses
            </Label>
          </div>

          <div className="p-4 bg-muted rounded-lg space-y-3">
            <div className="font-semibold text-sm">Template will include:</div>
            {activeItems.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {activeItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <Icon className={`w-4 h-4 ${item.color}`} />
                      <span className="truncate">
                        {item.label}
                        {item.count !== undefined && item.count > 0 && (
                          <span className="text-muted-foreground ml-1">({item.count})</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No configuration data available. Complete more workflow steps to include settings in the template.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between w-full sm:justify-between">
          <div className="flex items-center">
            {onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Template
              </Button>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !name.trim()}>
              {isSaving ? 'Saving...' : 'Save Template'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TemplateForm;
